import { useCallback, useEffect, useRef } from 'react';

/**
 * Manages a single AbortController instance, aborting any in-flight request
 * before replacing it with a new controller and cleaning it up on unmount.
 */
export const useResetControllerRef = () => {
  const controllerRef = useRef<AbortController | null>(null);

  const abortCurrent = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  const resetController = useCallback(() => {
    abortCurrent();
    const controller = new AbortController();
    controllerRef.current = controller;
    return controller;
  }, [abortCurrent]);

  const clearIfCurrent = useCallback((controller: AbortController) => {
    if (controllerRef.current === controller) controllerRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      abortCurrent();
    };
  }, [abortCurrent]);

  return {
    abortCurrent,
    clearIfCurrent,
    controllerRef,
    resetController,
  };
};
